import { useTranslate, useNotify } from 'react-admin';
import {
  Box,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@mui/material';
import { ContentCopy, FileDownload, ExpandMore } from '@mui/icons-material';

interface HandoffActionsProps {
  markdown: string;
  investigationId: string;
}

// "Copy debug handoff" + "Download .md" + a collapsed markdown preview (REF-06 s.5).
// Reused by both ReportCard and the history detail dialog. The handoff is the artifact a
// developer pastes into a source-aware coding agent; the operator is not required to read it.
export const HandoffActions = ({ markdown, investigationId }: HandoffActionsProps) => {
  const translate = useTranslate();
  const notify = useNotify();

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(markdown);
      notify(translate('aiTroubleshooting.handoff.copied'), { type: 'success' });
    } catch {
      notify(translate('aiTroubleshooting.handoff.copyFailed'), { type: 'error' });
    }
  };

  const onDownload = () => {
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `handoff-${investigationId}.md`; // REF-06 s.5 exact filename
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Button variant="contained" color="primary" startIcon={<ContentCopy />} onClick={onCopy}>
          {translate('aiTroubleshooting.handoff.copy')}
        </Button>
        <Button variant="outlined" color="primary" startIcon={<FileDownload />} onClick={onDownload}>
          {translate('aiTroubleshooting.handoff.download')}
        </Button>
      </Box>
      <Accordion disableGutters sx={{ '&:before': { display: 'none' }, boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="body2" color="text.secondary">
            {translate('aiTroubleshooting.handoff.preview')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ backgroundColor: 'grey.50' }}>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1,
              fontSize: '0.75rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflow: 'auto',
              maxHeight: 400,
            }}
          >
            {markdown}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default HandoffActions;
